import React, { useEffect, useState } from "react";
import { Box, Grid, styled, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useIntl } from "react-intl";
import EmailSidebar from "./EmailSidebar";
import EmailContent from "./EmailContent";
import { Email } from "../emailTypes";
import { useMails } from "../hooks/useMails";
import { useGameContext } from "../../context/GameContext";
import { WindowContent } from "../../desktopWindow/components/DesktopWindow";
import { AlertDialog, Feature } from "../../components/AlertDialog";
import { isDesktop, isMobile } from "react-device-detect";
import MobileHeadBar from "./HeadBar.mobile";
import DesktopHeadBar from "./HeadBar";
import { MobileHeadBarVariant } from "./HeadBar.mobile";
import { useSocketContext } from "context/SocketContext";
import { FileSources, ProtectedFilePayload } from "constants/sockets";
import { ActionBar } from "desktopWindow/components/DesktopWindow.styled";
import { ReactComponent as EditIcon } from "assets/svg/game-email-edit.svg";
import { ReactComponent as ReplyIcon } from "assets/svg/game-email-reply.svg";
import { ReactComponent as ArchiveIcon } from "assets/svg/game-email-archive.svg";

interface Props {}

const BOTTOM_BAR_HEIGHT = 90;

const EmailContainer: React.FC<Props> = () => {
	const { room } = useGameContext();
	const { mails, markMailAsRead } = useMails(room!.roomId);
	const classes = useStyles();
	const { socket } = useSocketContext();
	const intl = useIntl();
	const [mailToShow, setMailToShow] = useState<Email>();
	const [isAlertOpen, setAlertOpen] = React.useState<boolean>(false);

	// states for mobile only. they help to update the MobileHeadBar
	//action is what the user choose to see in them email app: inbox, trash etch
	const [action, setAction] = useState<string>(intl.formatMessage({ id: "EMAIL.headbar.mobile.mailbox" }));
	const [mobileHeadbarVariant, setMobileHeadbarVariant] = useState<MobileHeadBarVariant>(MobileHeadBarVariant.HOME);
	const [emailSubject, setEmailSubject] = useState<undefined | string>(mailToShow?.mailSubject);

	const handleShowMail = (emailId: string) => {
		const mail = mails.find((email) => email.id === emailId);
		setMailToShow(mail);
		markMailAsRead(emailId);
	};

	useEffect(() => {
		socket?.on("updateFileAcccess", (data: ProtectedFilePayload) => {
			if (data.fileSource === FileSources.EMAIL_ATTACHMENT) {
				// Only update the mail if the mail is currently shown in the UI
				setMailToShow((prevMail) => {
					return prevMail?.id === data.payload.id ? data.payload : prevMail;
				});
			}
		});
	}, [socket]);

	return (
		<>
			<EmailContainerHeader>
				<Grid container direction="row" alignItems="center" justifyContent="space-between">
					{isDesktop && <DesktopHeadBar setAlertOpen={setAlertOpen} mailToShow={mailToShow} />}
					{isMobile && (
						<MobileHeadBar
							mobileHeadbarVariant={mobileHeadbarVariant}
							action={action}
							emailSubject={emailSubject}
							setMobileHeadbarVariant={setMobileHeadbarVariant}
							setAction={setAction}
							setAlertOpen={setAlertOpen}
						/>
					)}
				</Grid>
			</EmailContainerHeader>
			<WindowContent style={{ backgroundColor: isMobile ? "#F2F2F7" : "#fff" }}>
				<Grid
					container
					direction="row"
					style={{ height: "100%", paddingBottom: isMobile ? BOTTOM_BAR_HEIGHT : 0 }}
				>
					{((isMobile && mobileHeadbarVariant !== MobileHeadBarVariant.EMAIL) || isDesktop) && (
						<Grid
							item
							container
							direction="column"
							md={6}
							xs={12}
							className={classes.emailSlideBarContainer}
						>
							<EmailSidebar
								emails={mails}
								onShowEmail={handleShowMail}
								selectedMail={mailToShow?.id}
								setAction={setAction}
								setMobileHeadbarVariant={setMobileHeadbarVariant}
								setEmailSubject={setEmailSubject}
								action={action}
								mobileHeadbarVariant={mobileHeadbarVariant}
							/>
						</Grid>
					)}

					{((isMobile && mobileHeadbarVariant === MobileHeadBarVariant.EMAIL) || isDesktop) && (
						<Grid
							item
							container
							direction="column"
							md={6}
							xs={12}
							className={classes.emailContentContainer}
						>
							<EmailContent mail={mailToShow} />
						</Grid>
					)}
				</Grid>
				{isMobile && (
					<Box
						sx={{
							height: BOTTOM_BAR_HEIGHT,
							width: "100%",
							position: "fixed",
							bottom: 0,
							backgroundColor: "#F7F7F7",
							display: "flex",
							justifyContent: "center",
						}}
					>
						{mobileHeadbarVariant === MobileHeadBarVariant.EMAIL ? (
							<Box
								sx={{
									display: "flex",
									flexDirection: "row",
									alignItems: "center",
									justifyContent: "space-around",
									width: "100%",
									padding: "0 16px",
								}}
							>
								<ArchiveIcon />
								<ReplyIcon />
								<EditIcon />
							</Box>
						) : (
							<Box
								sx={{
									display: "flex",
									flexDirection: "row",
									alignItems: "center",
									justifyContent: "center",
									position: "relative",
									width: "100%",
								}}
							>
								<Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>Updated just now</Typography>

								<Box position="absolute" right="16px">
									<EditIcon />
								</Box>
							</Box>
						)}
					</Box>
				)}
			</WindowContent>
			<AlertDialog isOpen={isAlertOpen} feature={Feature.EMAIL} onCancel={() => setAlertOpen(false)} />
		</>
	);
};

const EmailContainerHeader = styled(ActionBar)({
	padding: "8px",
});

export default EmailContainer;

const useStyles = makeStyles((theme) => ({
	emailSlideBarContainer: {
		height: "100%",
		// flexBasis: "41.66%",
		// maxWidth: "41.66%",
		// [theme.breakpoints.down("mobile")]: {
		// 	flexBasis: "100%",
		// 	maxWidth: "100%",
		// },
	},
	emailContentContainer: {
		height: "100%",
		overflowY: "scroll",
		// flexBasis: "58.33%",
		// maxWidth: "58.33%",
		// [theme.breakpoints.down("mobile")]: {
		// 	overflowY: "hidden",
		// 	flexBasis: mobileScreenWidth,
		// 	maxWidth: mobileScreenWidth,
		// },
	},
}));
